<script setup lang="ts">
import politicaPrivacidadeContent from '@/docs/POLITICA_PRIVACIDADE.md?raw'
import termosFornecedorContent from '@/docs/TERMOS_USO_FORNECEDORES.md?raw'
import termosOrganizadorContent from '@/docs/TERMOS_USO_ORGANIZADORES.md?raw'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()
const router = useRouter()

// Tipos disponíveis de termos
const tiposTermos = [
  { value: 'fornecedor', label: 'Fornecedores', icon: 'tabler-briefcase' },
  { value: 'organizador', label: 'Organizadores', icon: 'tabler-calendar-event' },
]

// Estado reativo
const activeTab = ref<string>(route.query.tab as string || 'termos')
const tipoSelecionado = ref<string>(route.query.tipo as string || 'fornecedor')

// Conteúdo dos termos baseado no tipo selecionado
const termosContent = computed(() => {
  if (tipoSelecionado.value === 'organizador')
    return termosOrganizadorContent

  return termosFornecedorContent
})

// Atualizar URL quando mudar de aba ou tipo
const handleTabChange = (value: unknown) => {
  const tab = value as string

  router.push({ query: { tab, tipo: tipoSelecionado.value } })
}

const handleTipoChange = (tipo: string) => {
  router.push({ query: { tab: activeTab.value, tipo } })
}

// Função para voltar
const goBack = () => {
  router.push('/')
}
</script>

<template>
  <div class="termos-page">
    <!-- Header -->
    <div class="termos-header">
      <VContainer class="py-6">
        <div class="mb-4 d-flex align-center">
          <VBtn
            variant="text"
            icon="tabler-arrow-left"
            @click="goBack"
          />
          <div class="ms-4">
            <h1 class="mb-1 text-h4 font-weight-bold">
              Termos e Políticas
            </h1>
            <p class="text-body-2 text-medium-emphasis">
              Conheça nossos termos de uso e política de privacidade
            </p>
          </div>
        </div>

        <!-- Seletor de tipo de usuário -->
        <div class="tipo-selector">
          <VBtnToggle
            v-model="tipoSelecionado"
            mandatory
            color="primary"
            rounded="lg"
            elevation="2"
            @update:model-value="handleTipoChange"
          >
            <VBtn
              v-for="tipo in tiposTermos"
              :key="tipo.value"
              :value="tipo.value"
              class="px-8 py-4 text-none"
              size="large"
              min-width="200"
            >
              <VIcon
                :icon="tipo.icon"
                start
                size="22"
              />
              {{ tipo.label }}
            </VBtn>
          </VBtnToggle>
        </div>
      </VContainer>
    </div>

    <!-- Conteúdo -->
    <VContainer class="py-8">
      <VCard elevation="2">
        <VTabs
          v-model="activeTab"
          class="mb-0"
          @update:model-value="handleTabChange"
        >
          <VTab
            value="termos"
            class="text-subtitle-1"
          >
            <VIcon
              icon="tabler-file-description"
              start
            />
            Termos de Uso
          </VTab>
          <VTab
            value="politica"
            class="text-subtitle-1"
          >
            <VIcon
              icon="tabler-shield-lock"
              start
            />
            Política de Privacidade
          </VTab>
        </VTabs>

        <VDivider />

        <VWindow v-model="activeTab">
          <VWindowItem value="termos">
            <div class="pa-6 termos-content">
              <div
                class="markdown-content"
                v-html="$md.render(termosContent)"
              />
            </div>
          </VWindowItem>

          <VWindowItem value="politica">
            <div class="pa-6 termos-content">
              <div
                class="markdown-content"
                v-html="$md.render(politicaPrivacidadeContent)"
              />
            </div>
          </VWindowItem>
        </VWindow>

        <VDivider />

        <VCardActions class="pa-4 d-flex justify-space-between">
          <VBtn
            variant="text"
            color="primary"
            @click="goBack"
          >
            <VIcon
              icon="tabler-arrow-left"
              start
            />
            Voltar
          </VBtn>

          <div class="gap-2 d-flex">
            <VBtn
              variant="outlined"
              color="primary"
              :href="`#${activeTab === 'termos' ? 'politica' : 'termos'}`"
              @click="activeTab = activeTab === 'termos' ? 'politica' : 'termos'"
            >
              Ver {{ activeTab === 'termos' ? 'Política de Privacidade' : 'Termos de Uso' }}
            </VBtn>
          </div>
        </VCardActions>
      </VCard>
    </VContainer>

    <!-- Footer informativo -->
    <VContainer class="pb-12">
      <VAlert
        type="info"
        variant="tonal"
        density="compact"
      >
        <template #prepend>
          <VIcon icon="tabler-info-circle" />
        </template>
        <div>
          <p class="mb-2 font-weight-medium">
            Última atualização: {{ new Date().toLocaleDateString('pt-BR') }}
          </p>
          <p class="text-sm">
            Ao utilizar nossa plataforma, você concorda com os termos de uso e política de privacidade.
            Em caso de dúvidas, entre em contato conosco através do e-mail
            <a
              href="mailto:contato@connecteventos.com.br"
              class="text-primary"
            >contato@connecteventos.com.br</a>
          </p>
        </div>
      </VAlert>
    </VContainer>
  </div>
</template>

<style scoped>
.termos-page {
  background:
    linear-gradient(
      to bottom,
      rgba(var(--v-theme-primary), 0.05) 0%,
      rgba(var(--v-theme-surface), 1) 300px
    );
  min-block-size: 100vh;
}

.termos-header {
  backdrop-filter: blur(10px);
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.1), rgba(var(--v-theme-secondary), 0.05));
  border-block-end: 1px solid rgba(var(--v-theme-outline), 0.2);
}

.tipo-selector {
  display: flex;
  justify-content: center;
}

.tipo-selector :deep(.v-btn-group) {
  overflow: visible;
  border-radius: 12px;
  gap: 8px;
}

.tipo-selector :deep(.v-btn) {
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 8%);
  font-weight: 600;
  letter-spacing: 0.025em;
  transition: all 0.3s ease;
}

.tipo-selector :deep(.v-btn--active) {
  box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 30%);
  transform: translateY(-2px);
}

.tipo-selector :deep(.v-btn:hover) {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 12%);
  transform: translateY(-1px);
}

.termos-content {
  max-block-size: 70vh;
  overflow-y: auto;
  scroll-behavior: smooth;
}

/* Scrollbar personalizada */
.termos-content::-webkit-scrollbar {
  inline-size: 8px;
}

.termos-content::-webkit-scrollbar-track {
  border-radius: 4px;
  background: rgba(var(--v-theme-surface-variant), 0.3);
}

.termos-content::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: rgba(var(--v-theme-primary), 0.4);
}

.termos-content::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--v-theme-primary), 0.6);
}

.markdown-content {
  color: rgb(var(--v-theme-on-surface));
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 0.9375rem;
  line-height: 1.75;
  text-align: justify;
  text-justify: inter-word;
}

/* Títulos principais */
.markdown-content :deep(h1) {
  border-block-end: 3px solid rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-primary));
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  margin-block: 0 2rem;
  margin-block-start: 1rem;
  padding-block-end: 0.75rem;
  text-align: center;
}

.markdown-content :deep(h2) {
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.1), transparent);
  border-inline-start: 4px solid rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-primary));
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  margin-block: 2.5rem 1.25rem;
  padding-block: 0.75rem;
  padding-inline: 1rem;
}

.markdown-content :deep(h3) {
  color: rgb(var(--v-theme-on-surface));
  font-size: 1.25rem;
  font-weight: 600;
  margin-block: 2rem 1rem;
}

.markdown-content :deep(h4) {
  color: rgb(var(--v-theme-on-surface-variant));
  font-size: 1.125rem;
  font-weight: 600;
  margin-block: 1.5rem 0.75rem;
}

/* Parágrafos */
.markdown-content :deep(p) {
  margin-block-end: 1.25rem;
  text-indent: 0;
}

/* Listas */
.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  margin-block: 1.25rem;
  padding-inline-start: 2rem;
}

.markdown-content :deep(li) {
  margin-block-end: 0.75rem;
  padding-inline-start: 0.5rem;
}

.markdown-content :deep(ul li) {
  list-style-type: disc;
}

.markdown-content :deep(ul li::marker) {
  color: rgb(var(--v-theme-primary));
  font-size: 1.1em;
}

.markdown-content :deep(ol li::marker) {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}

/* Texto com ênfase */
.markdown-content :deep(strong) {
  color: rgb(var(--v-theme-primary));
  font-weight: 700;
}

.markdown-content :deep(em) {
  color: rgb(var(--v-theme-secondary));
  font-style: italic;
}

/* Links */
.markdown-content :deep(a) {
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
  text-decoration: underline;
  text-decoration-color: rgba(var(--v-theme-primary), 0.3);
  text-decoration-thickness: 2px;
  text-underline-offset: 2px;
  transition: all 0.2s ease;
}

.markdown-content :deep(a:hover) {
  text-decoration-color: rgb(var(--v-theme-primary));
}

/* Citações */
.markdown-content :deep(blockquote) {
  border-radius: 0 8px 8px 0;
  background: rgba(var(--v-theme-primary), 0.05);
  border-inline-start: 4px solid rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-surface-variant));
  font-style: italic;
  margin-block: 1.5rem;
  padding-block: 1rem;
  padding-inline: 1.5rem;
}

/* Código inline */
.markdown-content :deep(code) {
  border: 1px solid rgba(var(--v-theme-primary), 0.2);
  border-radius: 4px;
  background-color: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
  font-family: "Fira Code", "Courier New", monospace;
  font-size: 0.875em;
  font-weight: 500;
  padding-block: 3px;
  padding-inline: 6px;
}

/* Blocos de código */
.markdown-content :deep(pre) {
  padding: 1.25rem;
  border: 1px solid rgba(var(--v-theme-outline), 0.3);
  border-radius: 8px;
  background-color: rgba(var(--v-theme-surface-variant), 0.7);
  margin-block: 1.5rem;
  overflow-x: auto;
}

.markdown-content :deep(pre code) {
  padding: 0;
  border: none;
  background-color: transparent;
  color: inherit;
}

/* Tabelas */
.markdown-content :deep(table) {
  overflow: hidden;
  border: 1px solid rgba(var(--v-theme-outline), 0.3);
  border-radius: 8px;
  border-collapse: separate;
  border-spacing: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 5%);
  inline-size: 100%;
  margin-block: 1.5rem;
}

.markdown-content :deep(thead) {
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.15), rgba(var(--v-theme-primary), 0.08));
}

.markdown-content :deep(th) {
  padding: 1rem;
  border-block-end: 2px solid rgba(var(--v-theme-primary), 0.3);
  color: rgb(var(--v-theme-primary));
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.025em;
  text-align: start;
  text-transform: uppercase;
}

.markdown-content :deep(td) {
  border-block-end: 1px solid rgba(var(--v-theme-outline), 0.2);
  padding-block: 0.875rem;
  padding-inline: 1rem;
}

.markdown-content :deep(tbody tr) {
  transition: background-color 0.2s ease;
}

.markdown-content :deep(tbody tr:hover) {
  background-color: rgba(var(--v-theme-primary), 0.03);
}

.markdown-content :deep(tbody tr:last-child td) {
  border-block-end: none;
}

/* Separadores */
.markdown-content :deep(hr) {
  position: relative;
  border: none;
  border-block-start: 2px solid rgba(var(--v-theme-outline), 0.2);
  margin-block: 2.5rem;
}

.markdown-content :deep(hr::before) {
  position: absolute;
  background: rgb(var(--v-theme-primary));
  block-size: 2px;
  content: "";
  inline-size: 60px;
  inset-block-start: -2px;
  inset-inline-start: 50%;
  transform: translateX(-50%);
}

/* Destaque especial para seções importantes */
.markdown-content :deep(p strong:first-child) {
  display: block;
  margin-block-end: 0.5rem;
}

/* Melhorias de acessibilidade */
.markdown-content :deep(*:focus-visible) {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

/* Responsividade */
@media (max-width: 960px) {
  .tipo-selector {
    overflow-x: auto;
  }

  .markdown-content :deep(h1) {
    font-size: 1.5rem;
  }

  .markdown-content :deep(h2) {
    font-size: 1.25rem;
  }

  .markdown-content {
    font-size: 0.875rem;
  }
}
</style>
